import React, { useEffect, useMemo, useRef } from 'react';
import {
    UncontrolledCollapse,
    Badge,
    Card,
    CardHeader,
    CardBody,
    Button,
    Nav,
    NavItem,
    NavLink,
    Alert,
    Container,
} from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { SrxApi } from '../api/SrxApi';
import { toast } from 'react-toastify';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useContext } from 'react';
import { CurrentTimeContext } from '../contexts/CurrentTimeContext';
import checkBold from '@iconify/icons-mdi/check-bold';
import closeThick from '@iconify/icons-mdi/close-thick';
import clockMinusOutline from '@iconify/icons-mdi/clock-minus-outline';
import numeric1CircleOutline from '@iconify/icons-mdi/numeric-1-circle-outline';
import { GrChatOption } from 'react-icons/gr';
import { UserContext } from '../contexts/UserContext';
import generatePDF, { printNarcoticsRefillTicket } from '../utility-services/reportGenerator';
import printerIcon from '@iconify/icons-mdi/printer';
import emailOutline from '@iconify/icons-mdi/email-outline';
import emailOpenOutline from '@iconify/icons-mdi/email-open-outline';
import useUpdateAlert from '../custom-hooks/useUpdateAlert';
import downloadIcon from '@iconify/icons-mdi/download';
import { SelectedPharmaciesContext } from '../contexts/SelectedPharmaciesContext';
import { SelectedFacilitiesContext } from '../contexts/SelectedFacilitiesContext';
import { decode } from 'he';
import TableRowDetailBody from './TableRowDetailBody';
import {
    ActionButton,
    ActionCommentModal,
    ActionSendModal,
    ActionDoNotSendModal,
    ActionQuestionModal,
    ActionLimitedApprovalModal,
} from './TableRowDetailContent/ActionControls';
import {
    capitalizeFirstLetter as sharedCapitalizeFirstLetter,
    checkAlertStatus as sharedCheckAlertStatus,
    checkQuantity as sharedCheckQuantity,
    checkSupply as sharedCheckSupply,
    generateFileName,
    getDateAndTime as sharedGetDateAndTime,
    getDateAndTimeStamp as sharedGetDateAndTimeStamp,
    getDateTime as sharedGetDateTime,
    getLastAlertStage as sharedGetLastAlertStage,
    getUserActionLabelForHeader as sharedGetUserActionLabelForHeader,
    parseTemporal as sharedParseTemporal,
    getAlertCommentsForDisplay,
} from './TableRowDetailSharedUtils';
import { submitUserAction } from './TableRowDetailSharedUserAction';

const WIDGET_ACTION_PERMISSION_KEY = {
    highCost: { ask: 'canActionHighCostAsk', approve: 'canActionHighCostApprove', deny: 'canActionHighCostDeny', limit: 'canActionHighCostLimit', once: 'canActionHighCostOnce' },
    highcostalerts: { ask: 'canActionHighCostAlertAsk', approve: 'canActionHighCostAlertApprove', deny: 'canActionHighCostAlertDeny', limit: 'canActionHighCostAlertLimit' },
    openBatch: { ask: 'canActionOpenBatchAsk' },
    nonCovered: { ask: 'canActionNonCoveredAsk' },
    priorAuth: { ask: 'canActionPriorAuthAsk' },
    theraInt: { ask: 'canActionTheraIntAsk' },
    admissionMeds: { ask: 'canActionAdmissionMedsAsk' },
    refillTooSoon: { ask: 'canActionRefillTooSoonAsk' },
    pumpPickup: { ask: 'canActionPumpPickupAsk' },
    narcoticsRefill: { ask: 'canActionNarcoticsRefillAsk' },
    alfNewScript: { ask: 'canActionAlfNewScriptAsk' },
};
const ACTION_TO_PERMISSION_TIER = { approve: 'approve', deny: 'deny', submitlimit: 'limit', approveonce: 'once' };

const checkPharmacyActionPermissions = (userType, requestActions, widgetName, pharmacies, pharmId) => {
    const pharms = pharmacies?.find((pharm) => pharm.pharmID === pharmId);
    if (!pharms) return [];
    const userPharmPermissions = userType === 'PharmacyUser' ? pharms.userPermissions : pharms.pharmPermissions;
    const tierMap = WIDGET_ACTION_PERMISSION_KEY[widgetName];
    return requestActions.filter(({ action }) => {
        if (action === 'ask') {
            if (widgetName === 'clarifications') {
                return true;
            }
            if (widgetName === 'highCostAlert') {
                return tierMap?.ask ? userPharmPermissions[tierMap.ask] : undefined;
            } else {
                return tierMap?.ask ? pharms.pharmPermissions[tierMap.ask] : undefined;
            }
        } else if (ACTION_TO_PERMISSION_TIER[action]) {
            const permissionKey = tierMap?.[ACTION_TO_PERMISSION_TIER[action]];
            return permissionKey ? userPharmPermissions[permissionKey] : undefined;
        } else {
            return true;
        }
    });
};

const isSameAlert = (a, b) => {
    if (!a || !b) return false;
    return (
        (a.id && b.id && a.id === b.id) ||
        (a._internalID && b._internalID && a._internalID === b._internalID) ||
        (a.highCostAlertsId && b.highCostAlertsId && a.highCostAlertsId === b.highCostAlertsId) ||
        (a.admissionMedsGroupID && b.admissionMedsGroupID && a.admissionMedsGroupID === b.admissionMedsGroupID) ||
        (a.rxNo && b.rxNo && a.rxNo === b.rxNo) ||
        (a.rxNumber && b.rxNumber && a.rxNumber === b.rxNumber)
    );
};

const TableRowDetailPage = () => {
    const { rxNo } = useParams();
    const { admissionMedsGroupID } = useParams();
    const location = useLocation();
    const getCategoryEndpointType = (category) => {
        const categoryMap = {
            highCost: 'highcost',
            highcostalerts: 'highcostalerts',
            nonCovered: 'noncovered',
            priorAuth: 'priorauth',
            therapeuticInt: 'therapeuticint',
            clarifications: 'openbatch',
            admissionMeds: 'admissionmeds',
            refillTooSoon: 'refilltoosoon',
            pumpPickup: 'pumppickup',
            narcoticsRefill: 'narcoticsrefill',
            alfNewScript: 'alfnewscriptneeded',
            alfCostResponse: 'alfnonactionordenial',
        };
        return categoryMap[category] || category.toLowerCase();
    };

    const [widget, setWidget] = useState({});
    const { user, setUser } = useContext(UserContext);
    const { pharmacies } = useContext(SelectedPharmaciesContext);
    const { facilities } = useContext(SelectedFacilitiesContext);
    const [activeTab, setActiveTab] = useState('detail');
    const [loadingUserAction, setLoadingUserAction] = useState(false);
    const [actionComment, setActionComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [noAlert, setNoAlert] = useState(false);
    const { updateReadStatus, updateArchivedReadStatus } = useUpdateAlert();

    const { currentTime } = useContext(CurrentTimeContext);

    const [commentModalInfo, setCommentModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [questionModalInfo, setQuestionModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [limitedApprovalModalInfo, setLimitedApprovalModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [sendModalInfo, setSendModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [doNotSendModalInfo, setDoNotSendModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [showNonMediprocityUserAlert, setShowNonMediprocityUserAlert] = useState();

    const [firstTimeScrolling, setFirstTimeScrolling] = useState(true);

    const category = useMemo(() => {
        const paths = location.pathname.split('/');
        const urlCategory = paths.length > 1 ? paths[1] : '';
        switch (urlCategory) {
            case 'therapeutic-interchange':
                return 'therapeuticInt';
            case 'admission-medication-review':
                return 'admissionMeds';
            case 'authorization':
                return 'highCost';
            case 'clarifications':
                return 'clarifications';
            case 'openbatch':
                return 'clarifications';
            case 'prior-authorization-medication':
                return 'priorAuth';
            case 'non-covered':
                return 'nonCovered';
            default:
                return urlCategory;
        }
    }, [location.pathname]);

    useEffect(() => {
        if (activeTab === 'actions' && firstTimeScrolling) {
            setFirstTimeScrolling(false);
            let container = document.querySelector('.action-display-simplebar-container .simplebar-content-wrapper');
            container && container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
        }
    }, [activeTab]);

    useEffect(() => {
        const fetchAlert = (isInitial = false) => {
            if (isInitial) {
                setLoading(true);
            }
            const alertId = admissionMedsGroupID || rxNo;
            if (!alertId) {
                if (isInitial) {
                    setLoading(false);
                    setNoAlert(true);
                }
                console.error('No alert ID provided');
                return;
            }
            const endpointType = getCategoryEndpointType(category);
            const singleAlertPath = `/alerts/${endpointType}/${alertId}?archived=false`;
            SrxApi.get(singleAlertPath)
                .then((res) => {
                    setWidget({ results: [res.data] });
                    if (isInitial) {
                        setLoading(false);
                        setNoAlert(false);
                    }
                })
                .catch((err) => {
                    console.error('Error fetching single alert:', err);
                    console.log('Falling back to list endpoint...');
                    let path = '';
                    switch (category) {
                        case 'clarifications':
                            path = '/openbatch';
                            break;
                        case 'pumpPickup':
                            path = '/pumppickup';
                            break;
                        case 'narcoticsRefill':
                            path = '/narcoticsrefill';
                            break;
                        case 'alfNewScript':
                            path = '/alfnewscriptneeded';
                            break;
                        case 'alfCostResponse':
                            path = '/alfnonactionordenial';
                            break;
                        default:
                            path = `/${category.toLowerCase()}`;
                    }
                    SrxApi.get(path)
                        .then((res) => {
                            setWidget(res.data);
                            if (isInitial) {
                                setLoading(false);
                            }
                        })
                        .catch((fallbackErr) => {
                            if (isInitial) {
                                setLoading(false);
                                setNoAlert(true);
                            }
                            console.error('Error fetching alerts:', fallbackErr);
                        });
                });
        };

        fetchAlert(true);

        const intervalId = setInterval(() => {
            fetchAlert(false);
        }, 2000);

        return () => clearInterval(intervalId);
    }, [category, rxNo, admissionMedsGroupID]);

    let stateName = '';
    let name = '';
    switch (category) {
        case 'highCost':
            stateName = 'highCostList';
            name = 'Authorization';
            break;
        case 'highcostalerts':
            stateName = 'highCostAlertsListFlat';
            name = 'High Cost Alert';
            break;
        case 'clarifications':
            stateName = 'openBatchList';
            name = 'Clarifications';
            break;
        case 'priorAuth':
            stateName = 'priorAuthList';
            name = 'Prior Authorization Medication';
            break;
        case 'nonCovered':
            stateName = 'nonCoveredList';
            name = 'Non-Covered';
            break;
        case 'therapeuticInt':
            stateName = 'therapeuticInterchangeList';
            name = 'Therapeutic Interchanged';
            break;
        case 'admissionMeds':
            stateName = 'admissionMedsList';
            name = 'Admission / Medication Review';
            break;
        case 'refillTooSoon':
            stateName = 'refillTooSoonList';
            name = 'Refill Too Soon';
            break;
        case 'narcoticsRefill':
            stateName = 'narcoticsRefillList';
            name = 'Narcotics Refill';
            break;
        case 'alfNewScript':
            stateName = 'alfNewScriptList';
            name = 'ALF - New Script Needed';
            break;
        case 'alfCostResponse':
            stateName = 'alfCostResponseList';
            name = 'ALF - High Cost Denials';
            break;
        case 'pumpPickup':
            stateName = 'pumpPickupList';
            name = 'Pump Pickup';
            break;
        default:
            stateName = 'results';
            break;
    }
    let alert = {};
    switch (stateName) {
        case 'admissionMedsList':
            alert = findAlert(widget['results'], 'admissionMedsGroupID', Number(admissionMedsGroupID));
            break;
        case 'results':
            alert = findAlert(widget['results'], 'rxNumber', Number(rxNo));
            break;
        case 'openBatchList':
            alert = findAlert(widget['results'], 'rxNo', Number(rxNo));
            break;
        default:
            alert = findAlert(widget['results'], 'rxNo', Number(rxNo));
    }

    function findAlert(array, key, value) {
        if (!array) {
            return null;
        } else {
            for (let i = 0; i < array.length; i++) {
                if (array[i][key] === value) {
                    return array[i];
                }
            }
        }
    }

    const tab = alert?.archiveDtTm ? 'archive' : 'active';

    const requestActionPermissionWidgetName = category;

    function parseTemporal(date) {
        return sharedParseTemporal(date);
    }

    function getDateTime(date) {
        return sharedGetDateTime(date);
    }

    function getDateAndTime(date) {
        return sharedGetDateAndTime(date);
    }

    function getDateAndTimeStamp(date) {
        return sharedGetDateAndTimeStamp(date);
    }

    function capitalizeFirstLetter(word) {
        return sharedCapitalizeFirstLetter(word);
    }

    const appendSubmittedAction = (submittedAction) => {
        setWidget((currentWidget) => ({
            ...currentWidget,
            results:
                currentWidget?.results?.map((item) =>
                    isSameAlert(item, alert)
                        ? {
                              ...item,
                              requestedActions: [...(item.requestedActions || []), submittedAction],
                          }
                        : item
                ) || [],
        }));
    };

    const handleUserAction = async (action, actionComment, widget, limitDays = null, username = null) => {
        await submitUserAction({
            action,
            actionComment,
            widget,
            limitDays,
            username,
            alert,
            user,
            setUser,
            setLoadingUserAction,
            setActionComment,
            toggleCommentModal,
            toggleQuestionModal,
            toggleLimitedApprovalModal,
            toggleSendModal,
            toggleDoNotSendModal,
            onActionSubmitted: appendSubmittedAction,
        });
    };

    const getUserActionLabelForHeader = (action = '') => sharedGetUserActionLabelForHeader(action);

    function difference2Parts(date) {
        const secs = Math.floor(Math.abs(currentTime - date) / 1000);
        const mins = Math.floor(secs / 60);
        const hours = Math.floor(mins / 60);
        const days = Math.floor(hours / 24);

        return {
            days: days,
            hours: hours % 24,
            hoursTotal: hours,
            minutesTotal: mins,
            minutes: mins % 60,
            seconds: secs % 60,
            secondsTotal: secs,
        };
    }

    function openCommentModal(action, color, label) {
        setCommentModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openSendModal(action, color, label) {
        setSendModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openDoNotSendModal(action, color, label) {
        setDoNotSendModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openQuestionModal(action, color, label) {
        setQuestionModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openLimitedApprovalModal(action, color, label) {
        setLimitedApprovalModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function toggleCommentModal() {
        setCommentModalInfo({
            isOpen: !commentModalInfo.isOpen,
            action: '',
        });
    }

    function toggleSendModal() {
        setSendModalInfo({
            isOpen: !sendModalInfo.isOpen,
            action: '',
        });
    }

    function toggleDoNotSendModal() {
        setDoNotSendModalInfo({
            isOpen: !doNotSendModalInfo.isOpen,
            action: '',
        });
    }

    function toggleQuestionModal() {
        setQuestionModalInfo({
            isOpen: !questionModalInfo.isOpen,
            action: '',
        });
    }

    function toggleLimitedApprovalModal() {
        setLimitedApprovalModalInfo({
            isOpen: !limitedApprovalModalInfo.isOpen,
            action: '',
        });
    }

    const handleActionButtons = (action) => {
        if (!user.mediprocityUser) return setShowNonMediprocityUserAlert(true);

        if (action.toLowerCase() === 'approve') {
            openCommentModal(action, 'success', 'Approve');
        } else if (action.toLowerCase() === 'deny') {
            openCommentModal(action, 'danger', 'Deny');
        } else if (action.toLowerCase() === 'ask') {
            openQuestionModal(action, 'warning', 'Discuss');
        } else if (action.toLowerCase() === 'approveonce') {
            openCommentModal(action, 'secondary', 'Approve Once');
        } else if (action.toLowerCase() === 'submitlimit') {
            openLimitedApprovalModal(action, 'warning', 'Limit Approval');
        } else if (action.toLowerCase() === 'send') {
            openSendModal(action, 'success', 'Send');
        } else if (action.toLowerCase() === 'donotsend') {
            openDoNotSendModal(action, 'danger', 'Do Not Send');
        }
    };
    const discussChatIcon = <GrChatOption size={22} />;

    const highCostActionButtons = [
        {
            icon: checkBold,
            title: 'Approve',
            action: 'approve',
            disable: false,
        },
        {
            icon: closeThick,
            title: 'Deny',
            action: 'deny',
            disable: false,
        },
        {
            icon: clockMinusOutline,
            title: 'Limit Days',
            action: 'submitlimit',
            disable: false,
        },
        {
            icon: numeric1CircleOutline,
            title: 'Approve Once',
            action: 'approveonce',
            disable: false,
        },
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const highCostAlertsActionButtons = [
        {
            icon: checkBold,
            title: 'Approve',
            action: 'approve',
            disable: false,
        },
        {
            icon: closeThick,
            title: 'Deny',
            action: 'deny',
            disable: false,
        },
        {
            icon: clockMinusOutline,
            title: 'Limit Days',
            action: 'submitlimit',
            disable: false,
        },
        {
            icon: numeric1CircleOutline,
            title: 'Approve Once',
            action: 'approveonce',
            disable: false,
        },
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const clarificationsActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const remainingWidgetsActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const archivedActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const admissionMedicationReviewActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const RTSActionButtons = [
        {
            icon: clockMinusOutline,
            title: 'Approve Until Refill Date',
            action: 'limitedApproval',
            disable: false,
        },
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const pumpPickupActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const narcoticsRefillActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const getNarcoticsStyleDiscussButtonsForPage = (pharmID) => {
        const permissionWidget = category === 'alfNewScript' ? 'narcoticsRefill' : requestActionPermissionWidgetName;
        const permitted = checkPharmacyActionPermissions(
            user.userType,
            narcoticsRefillActionButtons,
            permissionWidget,
            pharmacies,
            pharmID
        );
        return permitted?.length ? permitted : narcoticsRefillActionButtons;
    };

    const discussActionWidgetName = category === 'alfNewScript' ? 'alfNewScript' : 'narcoticsRefill';

    const changeReadStatus = async (status) => {
        function returnUrl() {
            switch (category) {
                case 'highCost':
                    return '/highcost_readByFaci';
                case 'highcostalerts':
                    return '/highcostalerts_readByFaci';
                case 'openbatch':
                    return '/openbatch_readByFaci';
                case 'clarifications':
                    return '/openbatch_readByFaci';
                case 'priorAuth':
                    return '/priorauth_readByFaci';
                case 'nonCovered':
                    return '/noncovered_readByFaci';
                case 'therapeuticInt':
                    return '/therapeuticint_readByFaci';
                case 'admissionMeds':
                    return '/admissionmeds_readByFaci';
                case 'refillTooSoon':
                    return '/refilltoosoon_readByFaci';
                case 'pumpPickup':
                    return '/pumppickup_readByFaci';
                case 'narcoticsRefill':
                    return '/narcoticsrefill_readByFaci';
                case 'alfNewScript':
                    return '/alfnewscriptneeded_readByFaci';
                case 'alfCostResponse':
                    return '/alfnonactionordenial_readByFaci';
            }
        }
        let id;
        switch (category) {
            case 'admissionMeds':
                id = alert.admissionMedsGroupID;
                break;
            case 'highcostalerts':
                id = alert?.highCostAlertsId;
            default:
                id = alert._internalID;
        }

        SrxApi.post(returnUrl(), {
            alertId: id,
            readByFaci: status,
        })
            .then((res) => {
                tab === 'archive'
                    ? updateArchivedReadStatus(status, alert, category)
                    : updateReadStatus(status, alert, category);
            })
            .catch((err) => toast.error('Error updating read status'));
    };

    const checkFacilityPermissionManually = () => {
        let facilityId = alert?.facID;
        let facilityList = facilities;
        return facilityList.find((facility) => {
            if (facilityId === facility.facID) {
                return (
                    facility.permissions?.['canMarkAlertReadManually'] &&
                    facility.permissions?.['canMarkAlertUnreadManually']
                );
            }
        });
    };

    function getLastAlertStage(alert) {
        return sharedGetLastAlertStage(alert);
    }
    function checkAlertStatus(alert) {
        return sharedCheckAlertStatus(alert);
    }

    const checkQuantity = (alert) => {
        return sharedCheckQuantity(alert);
    };

    const checkSupply = (alert) => {
        return sharedCheckSupply(alert);
    };

    if (!loading && (alert === undefined || !alert)) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '85vh' }}>
                <h2>No Active Alert Found</h2>
            </div>
        );
    }
    if (!loading)
        return (
            // <>hello</>
            <Container
                className="my-4 p-3 border border-light shadow-sm"
                style={{ borderRadius: '14px', width: '100%', minHeight: '410.61px' }}>
                <h3
                    className="text-center"
                    style={{
                        background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)',
                        color: 'white',
                    }}>
                    {name
                        .split(' ')
                        ?.map((word) => {
                            return word[0].toUpperCase() + word.substring(1);
                        })
                        .join(' ')}
                    {stateName === 'highCostList' && (
                        <Badge color={'secondary'} className="ml-1">
                            {getUserActionLabelForHeader(alert?.srxStatus)}
                        </Badge>
                    )}
                    {
                        <Button
                            title={alert?.readByFaci !== null ? 'Mark as unread' : 'Mark as read'}
                            hidden={!(checkFacilityPermissionManually() || category === 'alfNewScript')}
                            onClick={() => {
                                checkFacilityPermissionManually();
                                changeReadStatus(alert?.readByFaci !== null ? !alert?.readByFaci : false);
                            }}
                            className="link-button p-0 mb-1 ml-1"
                            color="link"
                            style={{ color: 'white', fontSize: '26px' }}>
                            <Icon icon={alert?.readByFaci ? emailOutline : emailOpenOutline} />
                        </Button>
                    }
                </h3>
                <Container>
                    {' '}
                    <TableRowDetailBody
                        alert={alert}
                        stateName={stateName}
                        category={category}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        requestActionPermissionWidgetName={requestActionPermissionWidgetName}
                        pharmacies={pharmacies}
                        getPermittedActions={(requestActions, widgetName, item) =>
                            checkPharmacyActionPermissions(
                                user.userType,
                                requestActions,
                                widgetName,
                                pharmacies,
                                item.pharmID
                            )
                        }
                        getDateTime={getDateTime}
                        getDateAndTime={getDateAndTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        capitalizeFirstLetter={capitalizeFirstLetter}
                        getUserActionLabelForHeader={getUserActionLabelForHeader}
                        getLastAlertStage={getLastAlertStage}
                        checkAlertStatus={checkAlertStatus}
                        checkQuantity={checkQuantity}
                        checkSupply={checkSupply}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        handleActionButtons={handleActionButtons}
                        commentModalInfo={commentModalInfo}
                        questionModalInfo={questionModalInfo}
                        limitedApprovalModalInfo={limitedApprovalModalInfo}
                        sendModalInfo={sendModalInfo}
                        doNotSendModalInfo={doNotSendModalInfo}
                        toggleCommentModal={toggleCommentModal}
                        toggleQuestionModal={toggleQuestionModal}
                        toggleLimitedApprovalModal={toggleLimitedApprovalModal}
                        toggleSendModal={toggleSendModal}
                        toggleDoNotSendModal={toggleDoNotSendModal}
                        handleUserAction={handleUserAction}
                        loadingUserAction={loadingUserAction}
                        actionWidgetNames={{
                            pumpPickup: 'pumpPickup',
                            refillTooSoon: 'refillTooSoon',
                            narcoticsDiscuss: category === 'alfNewScript' ? 'alfNewScript' : 'narcoticsRefill',
                        }}
                    />
                </Container>
            </Container>
        );
    return (
        <div className="page-loading-loader-div w-100 d-flex justify-content-center align-items-center">
            <h4>Loading Settings...</h4>
            <div className="bouncing-loader">
                <div></div>
                <div></div>
                <div></div>
            </div>
        </div>
    );
};

export default TableRowDetailPage;
